'use client'

import { useReportWebVitals } from 'next/web-vitals'

/**
 * Captures Core Web Vitals (LCP / INP / CLS / FCP / TTFB) in-app so fixes can be
 * verified per route, and — via `experimental.webVitalsAttribution` in next.config.js —
 * surfaces the *cause* of each one:
 *   - CLS → the element that shifted most
 *   - INP → the element interacted with + where the time went (input / processing / render)
 *   - LCP → the LCP element (and its image URL, if any)
 *
 *  - dev:  logs each metric + its culprit to the console for fast local diagnosis
 *  - prod: forwards to the GTM dataLayer and (if present) New Relic Browser as a
 *    PageAction, so vitals + culprits can be sliced by path in NRQL.
 * Renders nothing. Mirrors the GlobalClientEffect single-file client-effect pattern.
 */
type WebVitalsAttribution = {
    // CLS
    largestShiftTarget?: string
    largestShiftValue?: number
    // INP
    interactionTarget?: string
    interactionType?: string
    inputDelay?: number
    processingDuration?: number
    presentationDelay?: number
    // LCP
    element?: string
    url?: string
}

export default function WebVitalsReporter() {
    useReportWebVitals((metric) => {
        try {
            const { name, value, id } = metric
            const rating = (metric as { rating?: string }).rating
            const attribution = (metric as { attribution?: WebVitalsAttribution }).attribution || {}
            const target = attribution.largestShiftTarget || attribution.interactionTarget || attribution.element

            if (process.env.NODE_ENV !== 'production') {
                const v = name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`
                let cause = ''
                if (name === 'CLS' && attribution.largestShiftTarget) {
                    cause = `→ largest shift: ${attribution.largestShiftTarget}`
                } else if (name === 'INP' && attribution.interactionTarget) {
                    cause =
                        `→ ${attribution.interactionType} on ${attribution.interactionTarget} ` +
                        `(input ${Math.round(attribution.inputDelay || 0)} / ` +
                        `processing ${Math.round(attribution.processingDuration || 0)} / ` +
                        `render ${Math.round(attribution.presentationDelay || 0)} ms)`
                } else if (name === 'LCP' && attribution.element) {
                    cause = `→ element: ${attribution.element}${attribution.url ? ` [${attribution.url}]` : ''}`
                }
                // eslint-disable-next-line no-console
                console.log(`[web-vitals] ${name}: ${v} (${rating}) ${cause}`, attribution)
                return
            }

            if (typeof window === 'undefined') return
            const w = window as typeof window & {
                dataLayer?: Record<string, unknown>[]
                newrelic?: { addPageAction?: (name: string, attrs: Record<string, unknown>) => void }
            }

            w.dataLayer = w.dataLayer || []
            w.dataLayer.push({
                event: 'web_vitals',
                web_vitals_name: name,
                web_vitals_value: value,
                web_vitals_rating: rating,
                web_vitals_id: id,
                web_vitals_target: target,
                page_path: window.location.pathname
            })

            if (w.newrelic && typeof w.newrelic.addPageAction === 'function') {
                w.newrelic.addPageAction('webVitals', {
                    name,
                    value,
                    rating,
                    id,
                    target,
                    interactionType: attribution.interactionType,
                    path: window.location.pathname
                })
            }
        } catch {
            // Instrumentation must never break the page.
        }
    })

    return null
}
